import React, { useCallback, useEffect, useState } from "react";
import GenericTable from "components/table";
import { Role } from "types";
import { Trash, User as UserIcon, X, Check } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import api, { fetchRoles } from "api/axios";
import { useUserInfo } from "layouts/user-info/_context/UserinfoContext";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import TooltipHorizon from "components/tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { Image } from "components/Image";
import { useTranslation } from "react-i18next";

function SearchTableRoles({ tableData }: { tableData: Role[] }) {
  const { t } = useTranslation();
  const { userInfo, fetchUserRoles } = useUserInfo();
  const userId = userInfo.id;
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const { confirm } = useConfirmation();

  // All roles for multi-select
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesTotal, setRolesTotal] = useState(0);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesSkip, setRolesSkip] = useState(0);
  const ROLES_TAKE = 20;

  // Fetch all roles for multi-select
  const fetchRolesList = useCallback(
    async (reset = false) => {
      setRolesLoading(true);
      setRolesError(null);
      try {
        const data = await fetchRoles({
          take: ROLES_TAKE,
          skip: reset ? 0 : rolesSkip,
        });
        setRolesTotal(data.total);
        setRoles((prev) => (reset ? data.items : [...prev, ...data.items]));
        if (reset) setRolesSkip(ROLES_TAKE);
        else setRolesSkip((prev) => prev + ROLES_TAKE);
      } catch (err) {
        setRolesError(t("users.userInfo.errors.failedToFetchRoles"));
        handleAxiosAndCaughtError(err);
      } finally {
        setRolesLoading(false);
      }
    },
    [rolesSkip]
  );

  useEffect(() => {
    if (isAddDialogOpen) fetchRolesList(true);
  }, [isAddDialogOpen, fetchRolesList]);

  // Remove user from role
  const handleDeleteRow = async (roleId: string) => {
    const confirmed = await confirm({
      title: t("users.removeFromRole"),
      message: t("users.removeFromRoleMessage"),
      confirmText: t("users.remove"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });
    if (confirmed) {
      setDeletingRowId(roleId);
      try {
        // Replace with real API call
        await api.delete(`/users/${userId}/groups`, {
          data: { group_ids: [roleId] },
        });
        await fetchUserRoles();
        setDeletingRowId(null);
      } catch (err) {
        toast.error(t("users.failedToRemoveUserFromRole"));
        setDeletingRowId(null);
        handleAxiosAndCaughtError(err);
      }
    }
  };

  const handleAddRoles = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRoleIds.length === 0) return;
    setIsSubmitting(true);
    try {
      await api.post(`/users/${userId}/groups`, {
        group_ids: selectedRoleIds,
      });
      await fetchUserRoles();
      setIsSubmitting(false);
      setIsAddDialogOpen(false);
      setSelectedRoleIds([]);
      setIsRoleDropdownOpen(false);
      setRoleSearchTerm("");
    } catch (err) {
      toast.error(t("users.failedToAddUserToRoles"));
      setIsSubmitting(false);
      handleAxiosAndCaughtError(err);
    }
  };

  // Multi-select logic
  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const removeRole = (roleId: string) => {
    setSelectedRoleIds((prev) => prev.filter((id) => id !== roleId));
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(roleSearchTerm.toLowerCase())
  );

  const selectedRoles = roles.filter((role) =>
    selectedRoleIds.includes(role.id)
  );

  const columns: ColumnDef<Role, any>[] = [
    {
      accessorKey: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info: any) => (
        <span className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </span>
      ),
    },
    {
      accessorKey: "description",
      header: () => <p>{t("common.description")}</p>,
      cell: (info: any) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    },
    {
      accessorKey: "id",
      header: () => <></>,
      cell: (info: any) => (
        <TooltipHorizon
          extra=""
          placement="top"
          trigger={
            <button
              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
              onClick={() => {
                handleDeleteRow(info.getValue());
              }}
              disabled={deletingRowId?.trim().length > 0}
            >
              <Trash size={15} className="text-red-500" />
            </button>
          }
          content={<span>{t("users.removeFromRoleTooltip")}</span>}
        />
      ),
      meta: {
        cellClassName: "min-w-[50px]",
        headerClassName: "min-w-[50px]",
      },
    },
  ];

  const dataWithActions = tableData.map((row) => ({
    ...row,
    deletingRowId,
    handleDeleteRow,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        renderRowClassName={renderRowClassName}
        searchPlaceholder={t("users.searchRoles")}
        onAdd={() => setIsAddDialogOpen(true)}
        debug={true}
        addLabel={t("users.assignUserToRoles")}
      />
      <Dialog isOpen={isAddDialogOpen} setIsOpen={setIsAddDialogOpen}>
        <form
          onSubmit={handleAddRoles}
          className="flex min-h-[70vh] w-full min-w-[280px] max-w-[500px] flex-col gap-4 sm:min-w-[400px] sm:gap-6"
        >
          <div className="border-b border-gray-200 pb-3 sm:pb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("users.assignUserToRolesTitle")}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {t("users.assignUserToRoles")}
            </p>
          </div>

          {/* Selected Roles Display */}
          {selectedRoles.length > 0 && (
            <div className="mb-3 rounded-lg bg-gray-50 p-2.5 dark:bg-gray-800 sm:mb-4 sm:p-3">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {selectedRoles.slice(0, 3).map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center gap-1.5 rounded-full bg-brand-100 px-2.5 py-1 text-xs font-medium text-brand-800 dark:bg-brand-900 dark:text-brand-200 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm"
                  >
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="truncate">{role.name}</span>
                    <button
                      type="button"
                      onClick={() => removeRole(role.id)}
                      className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-brand-200 dark:hover:bg-brand-800 sm:ml-1"
                    >
                      <X size={10} className="sm:h-3 sm:w-3" />
                    </button>
                  </div>
                ))}
                {selectedRoles.length > 3 && (
                  <div className="flex items-center gap-1.5 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300 sm:gap-2 sm:px-3 sm:py-1.5 sm:text-sm">
                    <span>{t("users.userInfo.roles.moreRoles", { count: selectedRoles.length - 3 })}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const remainingRoles = selectedRoles
                          .slice(3)
                          .map((r) => r.name)
                          .join(", ");
                        toast.error(
                          t("users.userInfo.roles.otherSelectedRoles", { roles: remainingRoles })
                        );
                      }}
                      className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600 sm:ml-1"
                      title={t("users.userInfo.roles.viewAllSelectedRoles")}
                    >
                      <UserIcon size={10} className="sm:h-3 sm:w-3" />
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-2">
                {t("users.userInfo.roles.selectedRolesCount", { count: selectedRoles.length })}
              </p>
            </div>
          )}

          {/* Role Search and Selection */}
          <div className="relative">
            <div className="relative">
              <UserIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 sm:left-3 sm:h-4 sm:w-4" />
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500 sm:py-2.5 sm:pl-10 sm:pr-4"
                placeholder={t("users.searchRolesByNameOrDescription")}
                value={roleSearchTerm}
                onChange={(e) => setRoleSearchTerm(e.target.value)}
                onFocus={() => setIsRoleDropdownOpen(true)}
              />
            </div>
            {/* Role Dropdown */}
            {isRoleDropdownOpen && (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 sm:max-h-60">
                {rolesLoading && roles.length === 0 && (
                  <div className="flex items-center justify-center p-3 text-gray-500 sm:p-4">
                    {t("common.loading")}
                  </div>
                )}
                {rolesError && roles.length === 0 && (
                  <div className="flex flex-col items-center p-3 sm:p-4">
                    <span className="text-sm text-red-500">{rolesError}</span>
                    <button
                      type="button"
                      className="mt-1.5 text-sm text-blue-500 underline sm:mt-2"
                      onClick={() => fetchRolesList(true)}
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                )}
                {filteredRoles.length > 0 ? (
                  filteredRoles.map((role) => {
                    const isSelected = selectedRoleIds.includes(role.id);
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => handleRoleToggle(role.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 sm:gap-3 sm:px-4 sm:py-3 ${
                          isSelected ? "dark:bg-brand-900/50 bg-brand-50" : ""
                        }`}
                      >
                        <Image
                          className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-8 sm:w-8 sm:text-sm"
                          alt={role.name}
                        />

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-900 dark:text-white sm:text-sm">
                            {role.name}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                            {role.description}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400 sm:h-4 sm:w-4" />
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 sm:px-4 sm:py-3 sm:text-sm">
                    {t("users.noRolesFound")}
                  </div>
                )}
                {rolesError && roles.length > 0 && (
                  <div className="flex flex-col items-center p-1.5 sm:p-2">
                    <span className="text-xs text-red-500">{rolesError}</span>
                    <button
                      type="button"
                      className="mt-1 text-xs text-blue-500 underline"
                      onClick={() => fetchRolesList(false)}
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                )}
                {!rolesLoading &&
                  roles.length < rolesTotal &&
                  !rolesError && (
                    <button
                      type="button"
                      className="w-full p-1.5 text-center text-xs text-blue-500 hover:underline sm:p-2 sm:text-sm"
                      onClick={() => fetchRolesList(false)}
                    >
                      {t("users.loadMore")}
                    </button>
                  )}
                {rolesLoading && roles.length > 0 && (
                  <div className="flex items-center justify-center p-1.5 text-xs text-gray-400 sm:p-2">
                    {t("users.loadingMore")}
                  </div>
                )}
              </div>
            )}
            {/* Click outside to close dropdown */}
            {isRoleDropdownOpen && (
              <div
                className="fixed inset-0 z-0"
                onClick={() => setIsRoleDropdownOpen(false)}
              />
            )}
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <button
              type="button"
              onClick={() => {
                setIsAddDialogOpen(false);
                setSelectedRoleIds([]);
                setIsRoleDropdownOpen(false);
                setRoleSearchTerm("");
              }}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:px-4"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-brand-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting || selectedRoleIds.length === 0}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                  {t("users.assigning")}
                </div>
              ) : (
                t("users.assignToRoles")
              )}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export default SearchTableRoles;
